import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const NotificationsPanel = () => {
  const [notifications, setNotifications] = useState([
    {
      id: 'notif-001',
      type: 'verification_request',
      title: 'New Verification Request',
      message: 'TechCorp HR wants to verify your Software Engineering Certificate',
      timestamp: new Date('2025-09-02T17:45:00'),
      isRead: false,
      priority: 'high',
      actionRequired: true,
      metadata: {
        requester: 'TechCorp HR Department',
        credential: 'Software Engineering Certificate'
      }
    },
    {
      id: 'notif-002',
      type: 'credential_issued',
      title: 'Credential Successfully Issued',
      message: 'Your Digital Identity Proof has been issued and added to your wallet',
      timestamp: new Date('2025-09-02T16:20:00'),
      isRead: false,
      priority: 'medium',
      actionRequired: false,
      metadata: {
        issuer: 'Identity Verification Service',
        credential: 'Digital Identity Proof'
      }
    },
    {
      id: 'notif-003',
      type: 'verification_complete',
      title: 'Verification Completed',
      message: 'Your Professional Driver License was successfully verified by Ride Share Company',
      timestamp: new Date('2025-09-02T14:15:00'),
      isRead: true,
      priority: 'low',
      actionRequired: false,
      metadata: {
        verifier: 'Ride Share Company',
        credential: 'Professional Driver License'
      }
    },
    {
      id: 'notif-004',
      type: 'security_alert',
      title: 'Security Alert',
      message: 'Unusual verification activity detected from new location: Austin, TX',
      timestamp: new Date('2025-09-02T12:30:00'),
      isRead: false,
      priority: 'high',
      actionRequired: true,
      metadata: {
        location: 'Austin, TX',
        ipAddress: '198.51.100.15'
      }
    },
    {
      id: 'notif-005',
      type: 'credential_expiring',
      title: 'Credential Expiring Soon',
      message: 'Your Security Clearance Level 2 will expire in 30 days',
      timestamp: new Date('2025-09-02T10:00:00'),
      isRead: true,
      priority: 'medium',
      actionRequired: true,
      metadata: {
        credential: 'Security Clearance Level 2',
        expiryDate: '2026-08-15'
      }
    },
    {
      id: 'notif-006',
      type: 'system_update',
      title: 'System Update Available',
      message: 'New security features and performance improvements are ready to install',
      timestamp: new Date('2025-09-02T08:00:00'),
      isRead: true,
      priority: 'low',
      actionRequired: false,
      metadata: {
        version: 'v2.1.0',
        features: ['Enhanced encryption', 'Faster verification']
      }
    }
  ]);

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'verification_request': return 'Shield';
      case 'credential_issued': return 'FileText';
      case 'verification_complete': return 'CheckCircle';
      case 'security_alert': return 'AlertTriangle';
      case 'credential_expiring': return 'Clock';
      case 'system_update': return 'Download';
      default: return 'Bell';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-error bg-error/10';
      case 'medium': return 'text-warning bg-warning/10';
      case 'low': return 'text-success bg-success/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor(diff / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ago`;
    } else if (minutes > 0) {
      return `${minutes}m ago`;
    } else {
      return 'Just now';
    }
  };

  const markAsRead = (notificationId) => {
    setNotifications(prev => 
      prev?.map(notif => 
        notif?.id === notificationId 
          ? { ...notif, isRead: true }
          : notif
      )
    );
  };

  const markAllAsRead = () => {
    setNotifications(prev => 
      prev?.map(notif => ({ ...notif, isRead: true }))
    );
  };

  const unreadCount = notifications?.filter(n => !n?.isRead)?.length;

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <h2 className="text-xl font-semibold text-foreground">Notifications</h2>
          {unreadCount > 0 && (
            <div className="bg-error text-error-foreground text-xs font-medium px-2 py-1 rounded-full">
              {unreadCount} new
            </div>
          )}
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="ghost" size="sm" onClick={markAllAsRead}>
            Mark all read
          </Button>
          <Button variant="ghost" size="icon">
            <Icon name="Settings" size={16} />
          </Button>
        </div>
      </div>
      <div className="space-y-3 max-h-96 overflow-y-auto">
        {notifications?.map((notification) => (
          <div 
            key={notification?.id}
            className={`p-4 rounded-lg border transition-all duration-200 cursor-pointer hover:shadow-sm ${
              notification?.isRead 
                ? 'border-border bg-background' :'border-primary/20 bg-primary/5'
            }`}
            onClick={() => markAsRead(notification?.id)}
          >
            <div className="flex items-start space-x-3">
              <div className={`p-2 rounded-lg flex-shrink-0 ${getPriorityColor(notification?.priority)}`}>
                <Icon name={getNotificationIcon(notification?.type)} size={16} />
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between mb-1">
                  <h3 className={`font-medium text-sm ${notification?.isRead ? 'text-muted-foreground' : 'text-foreground'}`}>
                    {notification?.title}
                  </h3>
                  <div className="flex items-center space-x-2 flex-shrink-0 ml-2">
                    <span className="text-xs text-muted-foreground">
                      {formatTimestamp(notification?.timestamp)}
                    </span>
                    {!notification?.isRead && (
                      <div className="w-2 h-2 bg-primary rounded-full"></div>
                    )}
                  </div>
                </div>
                
                <p className={`text-sm mb-2 ${notification?.isRead ? 'text-muted-foreground' : 'text-foreground'}`}>
                  {notification?.message}
                </p>

                {notification?.actionRequired && (
                  <div className="flex items-center space-x-2 mt-3">
                    <Button variant="outline" size="sm">
                      <Icon name="Eye" size={14} className="mr-1" />
                      View Details
                    </Button>
                    {notification?.type === 'verification_request' && (
                      <>
                        <Button variant="default" size="sm">
                          <Icon name="Check" size={14} className="mr-1" />
                          Approve
                        </Button>
                        <Button variant="outline" size="sm">
                          <Icon name="X" size={14} className="mr-1" />
                          Decline
                        </Button>
                      </>
                    )}
                    {notification?.type === 'security_alert' && (
                      <Button variant="outline" size="sm">
                        <Icon name="Shield" size={14} className="mr-1" />
                        Review Security
                      </Button>
                    )}
                  </div>
                )}

                {/* Metadata Display */}
                <div className="mt-2 text-xs text-muted-foreground">
                  {notification?.type === 'verification_request' && (
                    <div>Requester: {notification?.metadata?.requester}</div>
                  )}
                  {notification?.type === 'credential_issued' && (
                    <div>Issuer: {notification?.metadata?.issuer}</div>
                  )}
                  {notification?.type === 'security_alert' && (
                    <div>Location: {notification?.metadata?.location} • IP: {notification?.metadata?.ipAddress}</div>
                  )}
                  {notification?.type === 'credential_expiring' && (
                    <div>Expires: {new Date(notification.metadata.expiryDate)?.toLocaleDateString()}</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {notifications?.length === 0 && (
        <div className="text-center py-8">
          <Icon name="Bell" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No notifications at this time.</p>
          <p className="text-sm text-muted-foreground mt-1">
            You'll receive updates about credential activities here.
          </p>
        </div>
      )}
    </div>
  );
};

export default NotificationsPanel;